import { ChangeEvent, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  ArrowCta,
  FormCard,
  FormControl,
  Input,
  Link,
  Text,
} from 'components/ui';
import { useUpdateAccountProfile } from '../../api/user/mutations/useUpdateAccountProfile';
import { AccountPage } from './AccountPage';
import { useAccountUser } from './useAccountUser';
import { LoadingScreen } from '../ui/LoadingScreen';

export const AccountProfile = () => {
  const { user, isLoading } = useAccountUser();
  const { mutation } = useUpdateAccountProfile();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [error, setError] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user || hasPrefilled) return;
    setFirstName(user.first_name ?? '');
    setLastName(user.last_name ?? '');
    setEmail(user.email ?? '');
    setHasPrefilled(true);
  }, [user, hasPrefilled]);

  if (isLoading || !user) return <LoadingScreen />;

  const missingName = !firstName.trim() || !lastName.trim();
  const missingEmail = !email.trim().includes('@');

  const updateField =
    (setValue: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      setSaved(false);
      setSaveFailed(false);
    };

  const onSubmit = async () => {
    setSaved(false);

    if (missingName) {
      setSaveFailed(false);
      setError('Enter your first and last name.');
      return;
    }

    if (missingEmail) {
      setSaveFailed(false);
      setError('Enter an email address.');
      return;
    }

    setError('');
    setSaveFailed(false);

    try {
      await mutation.mutateAsync({
        id: user.id,
        auth_id: user.auth_id,
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      setSaved(true);
    } catch {
      setSaveFailed(true);
    }
  };

  return (
    <AccountPage
      actionArea={
        <ArrowCta
          disabled={mutation.isLoading}
          loading={mutation.isLoading}
          onClick={onSubmit}
        >
          Save
        </ArrowCta>
      }
      intro="This is the information on your Ayble account."
      showBack
      testID="account-profile"
      title="Update your profile"
    >
      <FormCard>
        <FormControl required invalid={Boolean(error) && !firstName.trim()}>
          <FormControl.Label>First name</FormControl.Label>
          <Input
            data-testid="account-first-name"
            maxWidth="100%"
            onChange={updateField(setFirstName)}
            type="text"
            value={firstName}
          />
        </FormControl>

        <FormControl required invalid={Boolean(error) && !lastName.trim()}>
          <FormControl.Label>Last name</FormControl.Label>
          <Input
            data-testid="account-last-name"
            maxWidth="100%"
            onChange={updateField(setLastName)}
            type="text"
            value={lastName}
          />
        </FormControl>

        <FormControl required invalid={Boolean(error) && missingEmail}>
          <FormControl.Label>Email address</FormControl.Label>
          <Input
            data-testid="account-email"
            maxWidth="100%"
            onChange={updateField(setEmail)}
            type="email"
            value={email}
          />
          <FormControl.HelperText color="blueGray.600" mt="6px">
            Your login link is sent to this address.
          </FormControl.HelperText>
        </FormControl>

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}

        {saveFailed && (
          <Text color="red.500" fontSize="sm">
            Unable to update your information. Please contact{' '}
            <Link
              color="red.500"
              fontSize="sm"
              href="mailto:support@ayblehealth.com"
              isExternal={false}
            >
              support@ayblehealth.com
            </Link>
          </Text>
        )}

        {saved && (
          <Box bg="feather.200" px="15px" py="14px" rounded="16px">
            <Text>Your profile was saved.</Text>
          </Box>
        )}
      </FormCard>
    </AccountPage>
  );
};
